// Dev-only OSS-donation fixtures + their dispatcher, split out of `mocks.ts` on
// the `dashboard-mocks` precedent. Absent entirely until now, which is why BOTH
// `/donations` and `/donations/received` rendered nothing but `<ErrorState/>`
// under `PUBLIC_MOCKS` — and why `e2e/donations.spec.ts` was green over it: the
// spec asserts the `<h1>` and the CTA, and both render ABOVE the boundary.
//
// STATEFUL, on the `llm-routes` rule (`mocks.test.ts` header): every donation
// write is followed by a re-READ in the route (`gifts.reload()`), never a patch
// from the echo, because a gift's authority is its row. A stateless echo would
// therefore restore the pre-write list on the very next GET and make both dev
// stories lie about what detach / decline / pick just did.
//
// The two sides are DIFFERENT COLLECTIONS, not one filtered twice: `given` is
// what this caller gave, `received` is what repos they own were given, and the
// API authorizes them against different principals. Sharing one array here would
// invent a symmetry the server does not have.

import type { DonationRow, ModelOption } from './donations';
import { at } from './mock-session';
import { unhandledMockRoute } from './unhandled-mock-route';

/** A gift the caller GAVE. Money is the PENDING story — Stripe has not paid yet. */
const given: DonationRow[] = [
  {
    id: 'don_given_compute',
    kind: 'compute',
    target_kind: 'project',
    target_account_id: 'acc_mock01',
    target_repo_id: 'repo_1',
    target_repo_full_name: 'developerz-ai/api-gateway',
    runner_id: 'run_mock01',
    byok_key_id: null,
    chosen_model: null,
    active: true,
    created_iso: at('2026-07-02'),
    revoked_iso: null,
  },
  {
    // `active: false` with no `revoked_iso` is the PARKED-not-detached state: the
    // money gift Stripe has not confirmed. The inbox renders it on purpose.
    id: 'don_given_money',
    kind: 'money',
    target_kind: 'pool',
    target_account_id: null,
    target_repo_id: null,
    runner_id: null,
    byok_key_id: null,
    chosen_model: null,
    active: false,
    created_iso: at('2026-07-10'),
    revoked_iso: null,
  },
];

/** A gift the caller's PROJECT was given — the maintainer's inbox. */
const received: DonationRow[] = [
  {
    id: 'don_recv_llm',
    kind: 'llm',
    target_kind: 'project',
    target_account_id: 'acc_mock01',
    target_repo_id: 'repo_1',
    target_repo_full_name: 'developerz-ai/api-gateway',
    donor_login: 'octocat',
    runner_id: null,
    byok_key_id: 'key_mock01',
    // Unpicked, so the picker has something to do; the ceiling is what it offers.
    chosen_model: null,
    allowed_models: ['claude-sonnet-4-6', 'gpt-5.5'],
    active: true,
    created_iso: at('2026-07-05'),
    revoked_iso: null,
    key_spend_cap_usd: 50,
  },
  {
    id: 'don_recv_compute',
    kind: 'compute',
    target_kind: 'project',
    target_account_id: 'acc_mock01',
    target_repo_id: 'repo_2',
    target_repo_full_name: 'developerz-ai/docs-site',
    donor_login: 'hubot',
    runner_id: 'run_mock02',
    byok_key_id: null,
    chosen_model: null,
    active: true,
    created_iso: at('2026-06-18'),
    revoked_iso: null,
  },
];

/** The platform CATALOG the picker offers — a vocabulary, never an authorization. */
const modelOptions: readonly ModelOption[] = [
  { provider: 'anthropic', model: 'claude-opus-4-8' },
  { provider: 'anthropic', model: 'claude-sonnet-4-6' },
  { provider: 'openai', model: 'gpt-5.5' },
];

/** Park a row in place and hand back the stored truth, so the re-read sees it. */
function park(rows: DonationRow[], id: string): DonationRow | null {
  const i = rows.findIndex((r) => r.id === id);
  const row = rows[i];
  if (row === undefined) return null;
  const parked: DonationRow = { ...row, active: false, revoked_iso: at('2026-07-20') };
  rows[i] = parked;
  return parked;
}

/**
 * Handle a `/v1/donations*` request, or return `null` if `path` is not a
 * donation route (so `mockFetch` falls through). An unknown id REJECTS rather
 * than answering a synthesized row — the API folds unknown / foreign /
 * already-parked into one 404, and a mock that invented a row would hide the
 * 404 branch the screens are built around.
 */
export function donationsMock(
  path: string,
  method: string,
  init?: RequestInit,
): Promise<unknown> | null {
  const bare = path.split('?')[0] ?? path;
  if (bare !== '/v1/donations' && !bare.startsWith('/v1/donations/')) return null;
  const reject = (): Promise<never> => unhandledMockRoute(method, path);

  // `received` is a sibling collection, not an id — checked before the id parse.
  if (bare === '/v1/donations/received') {
    if (method !== 'GET') return reject();
    return Promise.resolve({ data: received, next_cursor: null, model_options: modelOptions });
  }
  if (bare === '/v1/donations') {
    if (method === 'GET') return Promise.resolve({ data: given, next_cursor: null });
    if (method !== 'POST') return reject();
    return Promise.resolve(mintGift(init));
  }

  const rest = bare.slice('/v1/donations/'.length);
  const [id = '', action = ''] = rest.split('/');
  // The donor's own revoke; the maintainer's refusal. Two principals, two rows.
  const answer = (row: DonationRow | null): Promise<unknown> =>
    row === null ? reject() : Promise.resolve(row);
  if (action === '' && method === 'DELETE') return answer(park(given, id));
  if (action === 'decline' && method === 'POST') return answer(park(received, id));
  if (action === 'model' && method === 'PATCH') return answer(pickModel(id, init));
  return reject();
}

/**
 * `POST /v1/donations` 201. A `money` gift answers `active: false` ALONGSIDE its
 * `checkout_url` — the gift is pending until Stripe says it was paid, and the
 * dev story has to be able to see that rather than a row that claims it is live.
 */
function mintGift(init?: RequestInit): DonationRow & { checkout_url?: string } {
  const body = init?.body
    ? (JSON.parse(String(init.body)) as { kind?: string; target?: string; runner_id?: string })
    : {};
  const kind = (body.kind ?? 'money') as DonationRow['kind'];
  const row: DonationRow = {
    id: `don_new_${given.length + 1}`,
    kind,
    target_kind: (body.target ?? 'pool') as DonationRow['target_kind'],
    target_account_id: null,
    target_repo_id: null,
    runner_id: body.runner_id ?? null,
    byok_key_id: null,
    chosen_model: null,
    active: kind !== 'money',
    created_iso: at('2026-07-20'),
    revoked_iso: null,
  };
  given.push(row);
  return kind === 'money' ? { ...row, checkout_url: 'https://checkout.example/devmock' } : row;
}

/** The RECIPIENT's pick. `null` CLEARS it and is sent explicitly, never by omission. */
function pickModel(id: string, init?: RequestInit): DonationRow | null {
  const i = received.findIndex((r) => r.id === id);
  const row = received[i];
  if (row === undefined) return null;
  const body = init?.body ? (JSON.parse(String(init.body)) as { model?: string | null }) : {};
  const picked: DonationRow = { ...row, chosen_model: body.model ?? null };
  received[i] = picked;
  return picked;
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
const PRISTINE_GIVEN = given.map((row) => structuredClone(row));
const PRISTINE_RECEIVED = received.map((row) => structuredClone(row));

export function resetDonationsMocks(): void {
  given.splice(0, given.length, ...PRISTINE_GIVEN.map((row) => structuredClone(row)));
  received.splice(0, received.length, ...PRISTINE_RECEIVED.map((row) => structuredClone(row)));
}
